import { Button } from "@helmr/ui";
import { useEffect, useState } from "react";
import { endSupportSession } from "../api/support";
import { hhmmOf } from "../lib/zoned";
import { useAppContext } from "./permissions";
import { useServices } from "./services";
import { strings } from "./strings";
import s from "./support-banner.module.css";

const t = strings.support;

/**
 * Shown on every screen of a Helmr support session (console-support): who is looking, that it is read only, when it
 * ends, and a way to end it now. Renders nothing for an ordinary staff session. The server is what makes the session
 * read only; this banner only says so.
 */
export function SupportBanner() {
  const ctx = useAppContext();
  const { client, session, queryClient } = useServices();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const support = ctx.me?.support;
  // The shells leave room for the banner through --support-bar, which the stylesheet sets while this attribute is present.
  const active = support !== undefined;
  useEffect(() => {
    if (!active) return;
    document.documentElement.setAttribute("data-support", "");
    return () => document.documentElement.removeAttribute("data-support");
  }, [active]);
  if (!support) return null;

  async function end() {
    setBusy(true); setFailed(false);
    try {
      await endSupportSession(client);
    } catch {
      // A 401 means the session is already over, which is what End asked for. Anything else: say so and stay.
      setFailed(true);
      setBusy(false);
      return;
    }
    session.signOut(null);
    queryClient.clear();
  }

  return (
    <div className={s.banner} role="region" aria-label={t.region} data-testid="support-session">
      <p className={s.text}>
        <strong>{t.title}</strong>
        <span aria-hidden="true"> · </span><span>{support.operatorName}</span>
        <span aria-hidden="true"> · </span><span className={s.nowrap}>{t.ends(hhmmOf(support.endsAt, ctx.timeZone))}</span>
        {failed ? <span className={s.err} role="alert"> {t.endFailed}</span> : null}
      </p>
      <Button busy={busy} busyLabel={t.ending} onClick={() => void end()}>{t.end}</Button>
    </div>
  );
}
